import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import store from './store/store';
import { refreshCurrentUser } from './store/slices/authSlice';
import Landing from './pages/Landing';
import LandingVehicleStep from './pages/LandingVehicleStep';
import LandingEstimateStep from './pages/LandingEstimateStep';
import Login from './pages/Login';
import Signup from './pages/Signup';
import PhoneVerification from './pages/PhoneVerification';
import EmailVerification from './pages/EmailVerification';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import CustomerDashboard from './pages/CustomerDashboard';
import DriverLogin from './pages/DriverLogin';
import DriverSignup from './pages/DriverSignup';
import DriverWallet from './pages/DriverWallet';
import DriverFinancePage from './pages/DriverFinancePage';
import DriverDashboard from './pages/DriverDashboard';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutRecoveryPage from './pages/CheckoutRecoveryPage';
import CustomerWallet from './pages/CustomerWallet';
import CustomerSupport from './pages/CustomerSupport';
import EmailNotifications from './pages/EmailNotifications';
import DeliveryFinance from './pages/DeliveryFinance';
import AccountClosure from './pages/AccountClosure';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import AppFooter from './components/AppFooter';
import ProtectedRoute from './components/ProtectedRoute';
import { ROUTES } from './config/appConfig';
import { getVerificationDestination } from './utils/verification';
import { normalizeUserRole } from './utils/apiResponseUtils';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';
import './styles/App.css';

const getDefaultRouteForRole = (user) => {
  const role = normalizeUserRole(user);

  if (role === 'admin') {
    return ROUTES.adminDashboard;
  }

  if (role === 'driver') {
    return ROUTES.driverDashboard;
  }

  return ROUTES.dashboard;
};

const AppRoutes = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user, restoring } = useSelector((state) => state.auth);
  const location = useLocation();
  const customerScopeKey=String(user?.id??user?.customerId??user?.userId??'customer');
  React.useEffect(() => { if (restoring) dispatch(refreshCurrentUser()); }, [dispatch, restoring]);
  const destination = getVerificationDestination(user, location.state?.from);
  const defaultRoute = location.state?.from && normalizeUserRole(user) !== 'admin'
    ? destination.to : getDefaultRouteForRole(user);
  const postLoginState = location.state?.from ? destination.state : {
    ...(location.state?.postLoginFocus ? { focusSection: location.state.postLoginFocus } : {}),
    ...(location.state?.fromLandingFlow ? { fromLandingFlow: true } : {}),
  };
  const hideFooter = [
    ROUTES.dashboard,
    ROUTES.checkout,
    ROUTES.driverDashboard,
    ROUTES.adminDashboard,
  ].some((route) => location.pathname.startsWith(route));

  if (restoring) return <div className="p-5 text-center" role="status">Restoring your session?</div>;
  return (
    <div className="app-shell">
      <div className="app-shell-content">
        <Routes>
            {/* Public Routes */}
            <Route path={ROUTES.home} element={<Landing />} />
            <Route path={ROUTES.bookVehicle} element={isAuthenticated ? <ProtectedRoute><LandingVehicleStep /></ProtectedRoute> : <LandingVehicleStep />} />
            <Route path={ROUTES.bookEstimate} element={isAuthenticated ? <ProtectedRoute><LandingEstimateStep /></ProtectedRoute> : <LandingEstimateStep />} />
            <Route
              path={ROUTES.login}
              element={isAuthenticated ? <Navigate to={defaultRoute} state={postLoginState} replace /> : <Login />}
            />
            <Route
              path={ROUTES.signup}
              element={isAuthenticated ? <Navigate to={defaultRoute} state={postLoginState} replace /> : <Signup />}
            />
            <Route
              path={ROUTES.adminLogin}
              element={isAuthenticated && normalizeUserRole(user) === 'admin' ? <Navigate to={ROUTES.adminDashboard} /> : <AdminLogin />}
            />

            <Route path={ROUTES.emailVerification} element={
              <ProtectedRoute allowedRoles={['user', 'driver']} requireVerification={false}>
                <EmailVerification />
              </ProtectedRoute>
            } />
            <Route path={ROUTES.phoneVerification} element={
              <ProtectedRoute allowedRoles={['user', 'driver']} requireVerification={false}>
                <PhoneVerification />
              </ProtectedRoute>
            } />
            {/* Protected Routes */}
            <Route path="/dashboard/checkout/:checkoutId" element={<ProtectedRoute allowedRoles={['user']} requirePhoneVerified><CheckoutRecoveryPage key={customerScopeKey+location.pathname}/></ProtectedRoute>}/>
            <Route path="/dashboard/booking-confirmed/:deliveryId" element={<ProtectedRoute allowedRoles={['user']} requirePhoneVerified><PaymentSuccessPage key={customerScopeKey+location.pathname}/></ProtectedRoute>}/>
            <Route path="/dashboard/account" element={<ProtectedRoute allowedRoles={['user']}><CustomerDashboard key={customerScopeKey}/></ProtectedRoute>}/>
<Route path="/dashboard/wallet" element={<ProtectedRoute allowedRoles={['user']} requirePhoneVerified><CustomerWallet key={customerScopeKey} /></ProtectedRoute>} />
<Route path="/dashboard/payments/:deliveryId" element={<ProtectedRoute allowedRoles={['user']} requirePhoneVerified><DeliveryFinance key={customerScopeKey+location.pathname} /></ProtectedRoute>} />
<Route path="/dashboard/account-closure" element={<ProtectedRoute allowedRoles={['user']} requirePhoneVerified><AccountClosure key={customerScopeKey} /></ProtectedRoute>} />
            <Route
              path={ROUTES.dashboard}
              element={
                <ProtectedRoute allowedRoles={['user']} redirectTo={ROUTES.login}>
                  <CustomerDashboard key={customerScopeKey} />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.orders}
              element={
                <ProtectedRoute allowedRoles={['user']} redirectTo={ROUTES.login}>
                  <CustomerDashboard key={customerScopeKey} />
                </ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.checkout}
              element={
                <ProtectedRoute allowedRoles={['user']} requirePhoneVerified redirectTo={ROUTES.login}>
                  <CheckoutPage key={customerScopeKey} /></ProtectedRoute>
              }
            />
            <Route
              path={ROUTES.paymentSuccess}
              element={
                <ProtectedRoute allowedRoles={['user']} requirePhoneVerified redirectTo={ROUTES.login}>
                  <PaymentSuccessPage key={customerScopeKey} /></ProtectedRoute>
              }
            />

{['/dashboard/notifications','/driver/notifications'].map(path=><Route key={path} path={path} element={<ProtectedRoute allowedRoles={path.startsWith('/driver')?['driver']:['user']} requireVerification={false}><EmailNotifications/></ProtectedRoute>}/>)}
{['/dashboard/support','/dashboard/support/:caseId'].map(path=><Route key={path} path={path} element={<ProtectedRoute allowedRoles={['user']} requireVerification={false}><CustomerSupport key={customerScopeKey}/></ProtectedRoute>}/>)}
<Route path={ROUTES.driverWallet} element={<ProtectedRoute allowedRoles={['driver']} redirectTo={ROUTES.driverLogin}><DriverWallet key={String(user?.id??user?.driverId??user?.userId??'driver')} /></ProtectedRoute>} />
            <Route path="/driver" element={<ProtectedRoute allowedRoles={['driver']} redirectTo={ROUTES.driverLogin}><Navigate to={ROUTES.driverDashboard} replace/></ProtectedRoute>} />
            {['trips','earnings','ledger','payouts','support'].map(section=><Route key={section} path={'/driver/'+section} element={<ProtectedRoute allowedRoles={['driver']} requireVerification={false} redirectTo={ROUTES.driverLogin}><DriverFinancePage section={section}/></ProtectedRoute>}/>)}
            <Route path="/driver/trips/:tripId" element={<ProtectedRoute allowedRoles={['driver']} requireVerification={false}><DriverFinancePage section="trips"/></ProtectedRoute>}/>
            <Route path="/driver/payouts/:payoutId" element={<ProtectedRoute allowedRoles={['driver']} requireVerification={false}><DriverFinancePage section="payouts"/></ProtectedRoute>}/>
            <Route path="/driver/support/:caseId" element={<ProtectedRoute allowedRoles={['driver']} requireVerification={false}><DriverFinancePage section="support"/></ProtectedRoute>}/>
            {/* Driver Routes */}
            <Route path={ROUTES.driverLogin} element={isAuthenticated ? <Navigate to={defaultRoute} state={postLoginState} replace /> : <DriverLogin />} />
            <Route path={ROUTES.driverSignup} element={isAuthenticated ? <Navigate to={defaultRoute} state={postLoginState} replace /> : <DriverSignup />} />
            <Route
              path={ROUTES.driverDashboard}
              element={
                <ProtectedRoute allowedRoles={['driver']} redirectTo={ROUTES.driverLogin}>
                  <DriverDashboard key={String(user?.id??user?.driverId??user?.userId??'driver')} />
                </ProtectedRoute>
              }
            />

            <Route
              path={ROUTES.adminDashboard}
              element={
                <ProtectedRoute allowedRoles={['admin']} redirectTo={ROUTES.adminLogin}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            {/* Catch all - redirect to landing */}
            <Route path="*" element={<Navigate to={ROUTES.home} />} />
        </Routes>
      </div>
      {!hideFooter && <AppFooter />}
    </div>
  );
};

const AppContent = () => {
  return (
    <Router>
      <AppRoutes />
      <ToastContainer
        position="top-right"
        autoClose={3500}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
      />
    </Router>
  );
};

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
